import { forwardRef, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Slide from "@mui/material/Slide";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { TransitionProps } from "@mui/material/transitions";

import { useGetProductContent } from "src/api/product-content";
import { useAuthContext } from "src/auth/hooks";
import Iconify from "src/components/iconify";
import { useBoolean } from "src/hooks/use-boolean";
import { useTranslate } from "src/locales";
import { usePathname, useRouter } from "src/routes/hooks";
import {
  ProductContentAudience,
  ProductContentContext,
  ProductContentItem,
  ProductContentLocale,
} from "src/types/product-content";

const Transition = forwardRef(function ProductContentTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SUPPORTED_LOCALES = new Set<ProductContentLocale>([
  "bg",
  "en",
  "fr",
  "ger",
  "gre",
  "hu",
  "srb",
]);

const INTL_LOCALES: Record<ProductContentLocale, string> = {
  bg: "bg-BG",
  en: "en-GB",
  fr: "fr-FR",
  ger: "de-DE",
  gre: "el-GR",
  hu: "hu-HU",
  srb: "sr-RS",
};

type ContentTab = "updates" | "manuals";

function normalizeLocale(language: string): ProductContentLocale {
  const candidate = language.split("-")[0] as ProductContentLocale;
  return SUPPORTED_LOCALES.has(candidate) ? candidate : "en";
}

function providerContext(providerType: string): ProductContentContext | null {
  if (
    providerType === "facility" ||
    providerType === "coach" ||
    providerType === "organiser"
  ) {
    return providerType;
  }
  return null;
}

function FeatureMarkdown({ content }: { content: string }) {
  return (
    <Box
      sx={{
        color: "text.secondary",
        "& h1": { typography: "h5", color: "text.primary", mt: 0, mb: 2 },
        "& h2": { typography: "subtitle1", color: "text.primary", mt: 2 },
        "& p": { my: 1 },
        "& ul, & ol": { pl: 3 },
        "& a": { color: "primary.main" },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </Box>
  );
}

function routeForAudience(
  item: ProductContentItem,
  audience: ProductContentAudience,
  context: ProductContentContext,
) {
  if (audience === "player") {
    return item.frontend_routes.find(
      (route) => !route.startsWith("/dashboard"),
    );
  }

  const dashboardRoutes = item.frontend_routes.filter((route) =>
    route.startsWith("/dashboard"),
  );
  return context === "organiser"
    ? dashboardRoutes.find((route) =>
        route.startsWith("/dashboard/organiser"),
      ) ?? dashboardRoutes.find((route) => route === "/dashboard")
    : dashboardRoutes.find(
        (route) => !route.startsWith("/dashboard/organiser"),
      );
}

export default function ProductContentDialog() {
  const dialog = useBoolean();
  const router = useRouter();
  const pathname = usePathname();
  const { currentProviderType } = useAuthContext();
  const { t, i18n } = useTranslate();
  const [tab, setTab] = useState<ContentTab>("updates");
  const [search, setSearch] = useState("");
  const isDashboard = pathname.startsWith("/dashboard");
  const audience: ProductContentAudience = isDashboard ? "admin" : "player";
  const context: ProductContentContext | null = isDashboard
    ? providerContext(currentProviderType)
    : "player";
  const locale = normalizeLocale(i18n.language);
  const {
    productContent,
    productContentLoading,
    productContentError,
    refetchProductContent,
  } = useGetProductContent(
    locale,
    audience,
    context ?? "player",
    dialog.value && context !== null,
  );

  const visibleItems = useMemo(() => {
    if (context === null) return [];
    const normalizedSearch = search.trim().toLocaleLowerCase();
    return productContent.filter((item) => {
      const manual = item.manuals.find((entry) => entry.audience === audience);
      const content = tab === "updates" ? item.release_note : manual?.content;
      if (!content) return false;
      if (!normalizedSearch) return true;
      return [item.title, item.summary, content, ...item.areas]
        .join(" ")
        .toLocaleLowerCase()
        .includes(normalizedSearch);
    });
  }, [audience, context, productContent, search, tab]);

  const handleOpen = () => {
    dialog.onTrue();
  };

  const openFeature = (route: string) => {
    dialog.onFalse();
    router.push(route);
  };

  return (
    <>
      <Tooltip title={t("product_content_tooltip")}>
        <IconButton
          onClick={handleOpen}
          aria-label={t("product_content_tooltip")}
          sx={{
            color: "info.main",
            transform: "scale(1.35)",
          }}
        >
          <Iconify icon="solar:stars-bold-duotone" />
        </IconButton>
      </Tooltip>

      <Dialog
        fullScreen
        open={dialog.value}
        onClose={dialog.onFalse}
        TransitionComponent={Transition}
        PaperProps={{ sx: { maxWidth: 1000 } }}
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Toolbar disableGutters>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5">{t("product_content_title")}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t("product_content_subtitle")}
              </Typography>
            </Box>
            <IconButton
              color="inherit"
              edge="end"
              onClick={dialog.onFalse}
              aria-label={t("close")}
            >
              <Iconify icon="mingcute:close-line" />
            </IconButton>
          </Toolbar>

          <Tabs
            value={tab}
            onChange={(_, value: ContentTab) => setTab(value)}
            sx={{ mt: 1 }}
          >
            <Tab value="updates" label={t("product_content_whats_new")} />
            <Tab value="manuals" label={t("product_content_manuals")} />
          </Tabs>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ py: 3 }}>
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="space-between"
            >
              <TextField
                size="small"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("product_content_search")}
                inputProps={{ "aria-label": t("product_content_search") }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: 1, sm: 320 }, ml: "auto" }}
              />
            </Stack>

            {productContentLoading && (
              <Stack alignItems="center" py={8}>
                <CircularProgress />
              </Stack>
            )}

            {productContentError && (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => refetchProductContent()}
                  >
                    {t("retry")}
                  </Button>
                }
              >
                {t("product_content_load_error")}
              </Alert>
            )}

            {!productContentLoading &&
              !productContentError &&
              visibleItems.length === 0 && (
                <Alert severity="info">{t("product_content_empty")}</Alert>
              )}

            {!productContentLoading &&
              !productContentError &&
              visibleItems.map((item) => {
                const manual = item.manuals.find(
                  (entry) => entry.audience === audience,
                );
                const content =
                  tab === "updates" ? item.release_note : manual?.content;
                const usedFallback =
                  tab === "updates"
                    ? item.release_note_used_fallback
                    : manual?.used_fallback;
                const route = routeForAudience(
                  item,
                  audience,
                  context ?? "player",
                );

                return (
                  <Box
                    key={item.id}
                    sx={{
                      p: { xs: 2, sm: 3 },
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography variant="h6">{item.title}</Typography>
                          {tab === "updates" && item.is_new && (
                            <Chip
                              size="small"
                              color="success"
                              label={t("new")}
                            />
                          )}
                          {usedFallback && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t("product_content_english_fallback")}
                            />
                          )}
                        </Stack>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          whiteSpace="nowrap"
                        >
                          {new Intl.DateTimeFormat(INTL_LOCALES[locale], {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }).format(new Date(`${item.released_at}T00:00:00`))}
                          {item.release_version
                            ? ` · v${item.release_version}`
                            : ""}
                        </Typography>
                      </Stack>

                      <Typography variant="body2" color="text.secondary">
                        {item.summary}
                      </Typography>

                      {content && <FeatureMarkdown content={content} />}

                      {route && (
                        <Box>
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={
                              <Iconify icon="eva:arrow-ios-forward-fill" />
                            }
                            onClick={() => openFeature(route)}
                          >
                            {t("product_content_open_feature")}
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                );
              })}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
